import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { normalizeCatalogPage } from '@/lib/normalize';
import { usePagedQuery } from '@/lib/paged';
import { LoadMore } from '@/ui/load-more';
import type { DatasetSummary } from '@/lib/contract';
import { collectionLabel, formatDayUpper, formatHours, modalityLine, orDash } from '@/lib/format';
import { PageHeader } from '@/ui/page-header';
import { useRealtimeRefresh } from '@/lib/realtime';
import { DatasetsTabs } from './datasets-layout';
import { Segmented } from '@/ui/segmented';
import { MicroLabel } from '@/ui/micro-label';
import { EmptyState } from '@/ui/empty-state';
import { ErrorState } from '@/ui/error-state';
import { QualityBlocks } from '@/domain/quality-blocks';
import { AvailabilityBadge } from '@/domain/availability-badge';

const VIEWS = [
  { value: 'table', label: 'Table' },
  { value: 'kanban', label: 'Kanban' },
  { value: 'pipeline', label: 'Pipeline' }
];

export function DatasetsCatalog() {
  const [view, setView] = useState('table');
  const page = usePagedQuery({
    queryKey: ['admin', 'datasets', 'catalog'],
    // Certified only. Without this predicate every folder at any stage rendered as a product card:
    // the availability badge returns null for an uncertified row so it silently vanished, and the
    // footer read "Cert --". A catalogue of things that are not yet products is not a catalogue.
    fetchPage: async cursor =>
      normalizeCatalogPage(
        await api(`/datasets?certified=true${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`)
      ),
    /**
     * Cross-tenant, so no realtime channel covers it, and certification is a deliberate operator
     * act rather than something that lands on its own -- a slow refresh is enough. The old faster
     * poll keyed on `uploading | evaluating` was tuned for work in flight, which is exactly what
     * this screen no longer shows.
     */
    refetchInterval: 60000
  });
  const { isPending, isError, error, refetch } = page;

  /**
   * The operator's own channel.
   *
   * Every other dataset channel is per tenant, and these listings are cross-tenant -- so this screen
   * held no subscription at all and learned about a finished run only on its next poll, up to a
   * minute later. For the one table whose job is "what needs a decision", that is the wrong minute.
   */
  useRealtimeRefresh('/datasets/subscription-token', [['admin', 'datasets', 'catalog']], { realm: 'admin' });

  const rows = page.rows;
  // Stated by the first page, because they describe the catalogue rather than the loaded slice.
  const totals = page.first?.totals;

  return (
    <div className="grid gap-5">
      <PageHeader
        title="Datasets"
        subtitle={
          totals
            ? `${totals.certified_product_count} certified products · ${totals.vendor_count} vendors · ${totals.total_hours.toLocaleString('en-US')} h total`
            : 'Catalog'
        }
        actions={<Segmented segments={VIEWS} value={view} onChange={setView} />}
      />

      <DatasetsTabs />

      {isError ? (
        <ErrorState error={error} onRetry={() => void refetch()} title="Could not load the catalog" />
      ) : view === 'table' ? (
        // An empty grid used to mean "no datasets", "still loading" and "the request failed"
        // all at once. Each now says which it is.
        isPending ? (
          <EmptyState title="Loading datasets…" />
        ) : rows.length === 0 ? (
          <EmptyState
            title="Nothing certified yet."
            note="A dataset appears here once an operator certifies it. Work in progress lives under Submissions."
          />
        ) : (
          <>
            <div className="grid grid-cols-3 gap-4 max-lg:grid-cols-2 max-sm:grid-cols-1">
              {rows.map(row => (
                <CatalogCard key={row.id} dataset={row} />
              ))}
            </div>
            <LoadMore
              shown={rows.length}
              total={totals?.certified_product_count}
              hasMore={page.hasMore}
              loading={page.loadingMore}
              onLoadMore={page.loadMore}
            />
          </>
        )
      ) : (
        <EmptyState title={`The ${view} view has no design yet.`} note="Screen intentionally left empty" />
      )}
    </div>
  );
}

/** One catalog card: title + code header, a spec table, then an hours/quality/cert footer. */
function CatalogCard({ dataset }: { dataset: DatasetSummary }) {
  const navigate = useNavigate();
  return (
    <article
      onClick={() => navigate(`/admin/datasets/${dataset.id}?tenant=${dataset.tenant_id}`)}
      className="cursor-pointer rounded border border-border bg-card px-5 py-4 transition-colors hover:border-border-strong"
    >
      <header className="flex items-start justify-between gap-3">
        <div className="grid gap-1">
          <h3 className="text-[15px] font-medium text-foreground">{dataset.name}</h3>
          <MicroLabel size="xs">{[dataset.dataset_code, dataset.version_label].filter(Boolean).join(' · ')}</MicroLabel>
        </div>
        <AvailabilityBadge availability={dataset.availability} />
      </header>

      <dl className="mt-4 grid gap-1.5">
        <Spec label="Modality" value={modalityLine(dataset.modalities)} />
        <Spec label="Collection" value={collectionLabel(dataset.collection_type)} />
        <Spec label="Format" value={orDash(dataset.format_label)} />
        <Spec label="Rig" value={orDash(dataset.rig)} />
      </dl>

      <footer className="mt-4 flex items-center gap-3">
        <span className="text-[19px] font-medium text-foreground">{formatHours(dataset.hours)}</span>
        <MicroLabel size="xs" tone="pass">
          Quality {dataset.quality_score ?? '—'}
        </MicroLabel>
        <QualityBlocks blocks={dataset.quality_blocks} size="sm" className="ml-auto" />
        <MicroLabel size="xs">Cert {formatDayUpper(dataset.certified_at)}</MicroLabel>
      </footer>
    </article>
  );
}

function Spec({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[86px_1fr] gap-2">
      <MicroLabel as="dt" size="xs">
        {label}
      </MicroLabel>
      <dd className="font-mono text-[11px] text-muted">{value}</dd>
    </div>
  );
}
